import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

type ModalSessaoEncerradaProps = {
  open: boolean;
  onClose: () => void;
};

export default function ModalSessaoEncerrada({ open, onClose }: ModalSessaoEncerradaProps) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const titleId = "modal-sessao-encerrada-title";
  const descId = "modal-sessao-encerrada-desc";

  useEffect(() => {
    if (!open || typeof window === "undefined") return;

    closeBtnRef.current?.focus();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || typeof window === "undefined") return null;

  const modal = (
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Title id={titleId}>Sessão encerrada</Title>

        <Text id={descId}>
          Sua sessão foi encerrada porque um novo login foi realizado em outro dispositivo.
        </Text>

        <Actions>
          <CloseBtn ref={closeBtnRef} onClick={onClose}>
            Entrar novamente
          </CloseBtn>
        </Actions>
      </Dialog>
    </Overlay>
  );

  return createPortal(modal, document.body);
}

const fadeIn = keyframes`
  from { opacity: 0 }
  to { opacity: 1 }
`;

const popIn = keyframes`
  from { transform: translateY(8px); opacity: 0 }
  to { transform: translateY(0); opacity: 1 }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: grid;
  place-items: center;
  padding: 16px;
  z-index: 50;
  animation: ${fadeIn} 120ms ease-out;
`;

const Dialog = styled.div`
  width: min(420px, 100%);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
  padding: 26px 24px 22px;
  text-align: center;
  animation: ${popIn} 150ms ease-out;
`;

const Title = styled.h2`
  margin: 0 0 10px 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #7d1416;
  line-height: 1.4;
`;

const Text = styled.p`
  margin: 0 0 20px;
  line-height: 1.6;
  color: #333;
`;

const Actions = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: center;
`;

const CloseBtn = styled.button`
  padding: 10px 18px;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
  background: #7d1416;
  color: #fff;
  transition: filter 120ms ease, transform 80ms ease, box-shadow 120ms ease;

  &:hover {
    filter: brightness(1.05);
  }
  &:focus-visible {
    outline: 2px solid #7d1416;
    outline-offset: 2px;
    box-shadow: 0 0 0 4px rgba(125, 20, 22, 0.2);
  }
  &:active {
    transform: translateY(1px);
  }
`;
